/**
 * Carries the page a visitor was on when they hit a sign-up CTA through the
 * Clerk modal and the onboarding flow, so the completion step can send them
 * back to it.
 *
 * The env-level NEXT_PUBLIC_CLERK_SIGN_UP_FORCE_REDIRECT_URL sends every modal
 * sign-up to /x/onboarding, which otherwise discards the origin page entirely.
 * Sign-in has no such force-redirect and simply returns to the given URL.
 */
export const ONBOARDING_RETURN_PARAM = "from";

// Landing back on any of these would either loop the visitor through
// onboarding again or drop them in the auth flow they just finished.
const BLOCKED_PREFIXES = ["/x/onboarding", "/sign-in", "/sign-up"];

const MAX_LABEL_LENGTH = 40;

/**
 * A leading "/" is not enough on its own: URL parsing strips TAB, LF and CR
 * *before* resolving, so "/<TAB>/evil.com" passes a naive prefix test and still
 * resolves to https://evil.com/. Rejecting every C0 control, space and
 * backslash closes that whole family at once — no legitimate return path
 * carries one unencoded.
 */
function hasUnsafePathChar(raw: string): boolean {
  for (const char of raw) {
    if (char === "\\" || char.charCodeAt(0) <= 0x20) return true;
  }
  return false;
}

/**
 * Accepts only same-origin relative paths. The value travels in a URL that can
 * be shared or hand-edited, so `//evil.com` and `/\evil.com` — which browsers
 * resolve as protocol-relative absolute URLs — must never reach a router.
 */
export function safeOnboardingReturnPath(
  raw: string | null | undefined
): string | undefined {
  if (!raw) return undefined;
  if (!raw.startsWith("/")) return undefined;
  if (raw.startsWith("//")) return undefined;
  if (hasUnsafePathChar(raw)) return undefined;
  const blocked = BLOCKED_PREFIXES.some(
    (prefix) =>
      raw === prefix ||
      raw.startsWith(`${prefix}/`) ||
      raw.startsWith(`${prefix}?`) ||
      raw.startsWith(`${prefix}#`)
  );
  if (blocked) return undefined;
  return raw;
}

/**
 * Builds the post-sign-up destination. The homepage is deliberately dropped:
 * the completion step's own city/store CTA is a better payoff than "go back to
 * the homepage", so carrying "/" would make the flow worse, not better.
 */
export function onboardingUrlWithReturn(
  path: string | null | undefined
): string {
  const returnPath = safeOnboardingReturnPath(path);
  if (!returnPath || returnPath === "/") return "/x/onboarding";
  return `/x/onboarding?${ONBOARDING_RETURN_PARAM}=${encodeURIComponent(returnPath)}`;
}

/**
 * The card name is already sitting in the return URL's filter param, so the
 * completion step can name the destination without a second param carrying
 * attacker-supplied display text. Anything unparseable returns undefined so
 * the caller falls back to its generic label rather than rendering garbage.
 */
export function returnPathCardLabel(
  returnPath: string | undefined
): string | undefined {
  if (!returnPath) return undefined;
  const queryStart = returnPath.indexOf("?");
  if (queryStart === -1) return undefined;
  const params = new URLSearchParams(returnPath.slice(queryStart + 1));
  for (const [key, value] of params) {
    if (!key.endsWith("_q")) continue;
    const label = value.trim();
    if (!label) continue;
    return label.length > MAX_LABEL_LENGTH
      ? `${label.slice(0, MAX_LABEL_LENGTH).trimEnd()}…`
      : label;
  }
  return undefined;
}
